export function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize: '11px',
      fontWeight: '500',
      color: 'var(--text-3)',
      textTransform: 'uppercase',
      letterSpacing: '1.5px',
      fontFamily: 'var(--font-mono)',
      marginBottom: '14px',
    }}>
      {children}
    </div>
  );
}

export function Card({ children, style = {}, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        padding: '16px 18px',
        transition: 'border-color 0.15s',
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
      onMouseEnter={onClick ? e => e.currentTarget.style.borderColor = 'var(--border-med)' : undefined}
      onMouseLeave={onClick ? e => e.currentTarget.style.borderColor = 'var(--border)' : undefined}
    >
      {children}
    </div>
  );
}

export function MetricCard({ label, value, color }) {
  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: '12px',
      padding: '18px 20px',
      textAlign: 'center',
    }}>
      <div style={{
        fontSize: '28px',
        fontWeight: '700',
        fontFamily: 'var(--font-display)',
        color: color || 'var(--text-1)',
        lineHeight: 1,
        marginBottom: '6px',
      }}>{value}</div>
      <div style={{ fontSize: '11px', color: 'var(--text-3)', fontFamily: 'var(--font-mono)', letterSpacing: '0.5px' }}>{label}</div>
    </div>
  );
}

export function ScoreBar({ label, value, max = 100 }) {
  const pct = Math.round((value / max) * 100);
  const color = pct >= 70 ? 'var(--good)' : pct >= 50 ? 'var(--moderate)' : 'var(--poor)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
      <div style={{ fontSize: '13px', color: 'var(--text-2)', width: '180px', flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '3px', transition: 'width 0.8s ease' }} />
      </div>
      <div style={{ fontSize: '13px', fontFamily: 'var(--font-mono)', color, width: '36px', textAlign: 'right' }}>{pct}</div>
    </div>
  );
}

export function Badge({ children, type = 'default' }) {
  const colors = {
    default:  { bg: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' },
    fast:     { bg: 'rgba(0,229,160,0.1)',    color: 'var(--good)' },
    deep:     { bg: 'rgba(0,180,216,0.1)',    color: 'var(--info)' },
    crm:      { bg: 'rgba(255,209,102,0.1)',  color: 'var(--moderate)' },
    generate: { bg: 'rgba(255,107,107,0.1)',  color: 'var(--poor)' },
  };
  const c = colors[type] || colors.default;
  return (
    <span style={{
      display: 'inline-block',
      fontSize: '10px',
      padding: '2px 8px',
      borderRadius: '20px',
      background: c.bg,
      color: c.color,
      fontFamily: 'var(--font-mono)',
      letterSpacing: '0.3px',
    }}>
      {children}
    </span>
  );
}

export function RunButton({ children, onClick, full, secondary }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: full ? '100%' : undefined,
        padding: '11px 20px',
        background: secondary ? 'transparent' : 'var(--accent)',
        border: secondary ? '1px solid var(--border-med)' : 'none',
        borderRadius: '8px',
        color: secondary ? 'var(--text-2)' : '#0a0a0f',
        fontSize: '13px',
        fontWeight: '600',
        fontFamily: 'var(--font-display)',
        cursor: 'pointer',
        letterSpacing: '0.3px',
        transition: 'opacity 0.15s, transform 0.1s',
      }}
      onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
      onMouseLeave={e => e.currentTarget.style.opacity = '1'}
      onMouseDown={e => e.currentTarget.style.transform = 'scale(0.98)'}
      onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
    >
      {children}
    </button>
  );
}

export function CommandPreview({ cmd, url }) {
  return (
    <div style={{
      fontFamily: 'var(--font-mono)',
      fontSize: '13px',
      color: 'var(--accent)',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: '8px',
      padding: '12px 16px',
      wordBreak: 'break-all',
    }}>
      <span style={{ color: 'var(--text-3)' }}>$ </span>
      {cmd} <span style={{ color: 'var(--text-2)' }}>{url}</span>
    </div>
  );
}

export function StatusPill({ status }) {
  const map = {
    lead:     { label: 'Lead',     bg: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' },
    audit:    { label: 'Audit',    bg: 'rgba(255,209,102,0.1)', color: 'var(--moderate)' },
    proposal: { label: 'Proposal', bg: 'rgba(0,180,216,0.1)',   color: 'var(--info)' },
    active:   { label: 'Active',   bg: 'rgba(0,229,160,0.1)',   color: 'var(--good)' },
    churned:  { label: 'Churned',  bg: 'rgba(255,107,107,0.1)', color: 'var(--poor)' },
    lost:     { label: 'Lost',     bg: 'rgba(255,107,107,0.08)', color: 'var(--poor)' },
  };
  const m = map[status] || map.lead;
  return (
    <span style={{
      display: 'inline-block',
      fontSize: '11px',
      padding: '3px 10px',
      borderRadius: '20px',
      background: m.bg,
      color: m.color,
      fontFamily: 'var(--font-mono)',
    }}>
      {m.label}
    </span>
  );
}
